// NodeFormsList.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './NodeFormsList.css';

// Icons
import Icon from 'svg/Icon';

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * NodeFormsList
 *
 * Compact Peek list of form templates for a node.
 *
 * @returns {ReactNode}
 */
const NodeFormsList = React.memo(({
  forms = [],
  onSelectForm,
}) => {
  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- MEMOS ----- //

  const items = useMemo(() => (
    (Array.isArray(forms) ? forms : []).map((form) => {
      const isDefault = Boolean(form?.isDefault);
      const description = normalizeString(form?.description);

      return {
        key: form.key,
        title: normalizeString(form?.title) || 'Untitled Form',
        meta: description || (isDefault ? 'Default record fields' : 'Custom form'),
        iconKey: isDefault ? 'dynamic_form' : 'edit_note',
      };
    }).filter((item) => item.key)
  ), [forms]);

  // ----- RENDER ----- //

  return (
    <div className="node-forms-list">
      {items.map((item) => (
        <button
          key={item.key}
          className="node-forms-list-item"
          type="button"
          onClick={() => onSelectForm?.(item.key)}
        >
          <div
            className="node-forms-list-item-icon"
            style={{
              backgroundColor: theme.backgroundMuted,
            }}
          >
            <Icon
              iconKey={item.iconKey}
              color={theme.foreground}
              width={20}
              height={20}
            />
          </div>

          <div className="node-forms-list-item-copy">
            <div className="node-forms-list-item-title">
              {item.title}
            </div>
            <div
              className="node-forms-list-item-meta"
              style={{ color: theme.foregroundMuted }}
            >
              {item.meta}
            </div>
          </div>

          <Icon
            iconKey="chevron_right"
            color={theme.foregroundMuted}
            width={18}
            height={18}
          />
        </button>
      ))}
    </div>
  );
});

NodeFormsList.displayName = 'NodeFormsList';

export default NodeFormsList;
